import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Package, Calendar, FileText, Thermometer, AlertCircle, FlaskConical, BookOpen, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
    try {
      const hubRes = await axios.get(`${API}/products/${id}/hub`);
      setHub(hubRes.data?.hub || null);
    } catch (error) {
      setHub(null);
    }
  };

  const handleAddToCart = () => {
    if (product?.coming_soon) return;
    addToCart(product, quantity);
    navigate('/products');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-600"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product not found</h2>
          <button
            onClick={() => navigate('/products')}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/products')}
          data-testid="back-button"
          className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Products</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Product Image */}
          <div className="bg-white rounded-xl shadow-sm p-8 relative">
            {product.coming_soon && (
              <div className="absolute top-4 right-4 z-10 bg-amber-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md" data-testid="coming-soon-badge-detail">
                Coming Soon
              </div>
            )}
            {(() => {
              const gallery = (product.images && product.images.length > 0)
                ? product.images
                : (product.image_url ? [product.image_url] : []);
              const idx = Math.min(selectedImage, Math.max(0, gallery.length - 1));
              const current = gallery[idx];
              const resolveSrc = (u) => u && !u.startsWith('http') ? `${BACKEND_URL}${u}` : u;
              const optimized = (u, w) => {
                const src = resolveSrc(u);
                if (src && src.includes('/api/images/products/')) {
                  return `${src}${src.includes('?') ? '&' : '?'}w=${w}`;
                }
                return src;
              };
              return (
                <>
                  <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl flex items-center justify-center overflow-hidden">
                    {current ? (
                      <img
                        src={optimized(current, 800)}
                        alt={product.name}
                        decoding="async"
                        className="max-w-full max-h-full object-contain"
                        data-testid="product-detail-main-image"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-gray-300">
                        <FlaskConical className="w-32 h-32" strokeWidth={1.25} />
                        {product.coming_soon && (
                          <span className="mt-3 text-sm text-gray-400 font-medium uppercase tracking-wider">Image Soon</span>
                        )}
                      </div>
                    )}
                  </div>
                  {gallery.length > 1 && (
                    <div className="grid grid-cols-6 gap-2 mt-3" data-testid="product-detail-thumbnails">
                      {gallery.map((url, i) => (
                        <button
                          key={`${url}-${i}`}
                          type="button"
                          onClick={() => setSelectedImage(i)}
                          data-testid={`product-detail-thumb-${i}`}
                          className={`aspect-square bg-gray-50 rounded-lg overflow-hidden border-2 transition ${
                            i === idx ? 'border-blue-600 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-400'
                          }`}
                        >
                          <img src={optimized(url, 160)} alt={`View ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-contain" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
            <p className="text-xs text-gray-400 text-center mt-3 italic">
              *Illustrative image. Vial colors may vary by batch.
            </p>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-8">
              {/* Category Badge */}
              <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 text-sm font-semibold rounded-full mb-4">
                {product.category}
              </span>

              <h1 className="text-3xl font-bold text-gray-900 mb-4" data-testid="product-detail-name">
                {product.name}
              </h1>

              <p className="text-gray-600 mb-6">{product.description}</p>

              {hub && (
                <a
                  href={`/stacks/${hub.slug}`}
                  data-testid="product-hub-link"
                  className="group flex items-center justify-between gap-3 mb-6 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 transition-all hover:border-blue-400 hover:shadow-md"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white">
                      <BookOpen className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">View protocols for this peptide</p>
                      <p className="text-xs text-blue-700">{hub.title}</p>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-blue-600 transition-transform group-hover:translate-x-1" />
                </a>
              )}

              {/* Specifications */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-gray-600">Type:</span>
                  <span className="font-semibold text-gray-900">{product.product_type}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-gray-600">Purity:</span>
                  <span className="font-semibold text-gray-900">{product.purity}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-gray-600">Dosage:</span>
                  <span className="font-semibold text-gray-900">{product.dosage}</span>
                </div>
              </div>

              {/* Price and Add to Cart */}
              <div className="bg-gray-50 rounded-lg p-6">
                {product.coming_soon ? (
                  <div className="text-center" data-testid="coming-soon-block">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-amber-100 rounded-full mb-3">
                      <FlaskConical className="w-7 h-7 text-amber-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">Coming Soon</h3>
                    <p className="text-sm text-gray-600 mb-4">This product will be available soon. Stay tuned for updates.</p>
                    <button
                      disabled
                      data-testid="coming-soon-cta-detail"
                      className="w-full flex items-center justify-center space-x-2 bg-gray-200 text-gray-500 font-semibold py-3 rounded-lg cursor-not-allowed"
                    >
                      <span>Not Available Yet</span>
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center space-x-4 mb-4">
                      <span className="text-gray-600">Quantity:</span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          data-testid="decrease-quantity"
                          className="w-10 h-10 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-50"
                        >
                          -
                        </button>
                        <span className="w-12 text-center font-medium" data-testid="quantity-display">{quantity}</span>
                        <button
                          onClick={() => setQuantity(quantity + 1)}
                          data-testid="increase-quantity"
                          className="w-10 h-10 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-50"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={handleAddToCart}
                      data-testid="add-to-cart-button"
                      className="w-full flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors"
                    >
                      <ShoppingCart className="w-5 h-5" />
                      <span>Add to Cart</span>
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Product Details Tabs */}
            <div className="bg-white rounded-xl shadow-sm p-8 space-y-6">
              {/* Storage Information */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <Thermometer className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-gray-900">Storage Information</h3>
                </div>
                <p className="text-gray-600 text-sm" data-testid="storage-info">
                  {product.storage_info}
                </p>
              </div>

              {/* Label Information */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-gray-900">Label Information</h3>
                </div>
                <p className="text-gray-500 text-sm mb-3">Each vial label should contain:</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Product Name:</span>
                    <span className="font-semibold text-gray-900">{product.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dosage:</span>
                    <span className="font-semibold text-gray-900">{product.dosage}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Expiry Date:</span>
                    <span className="font-semibold text-gray-900">See label</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Usage Notice:</span>
                    <span className="font-semibold text-gray-900">For Research Use Only</span>
                  </div>
                </div>
              </div>

              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-yellow-900 mb-1">For Research Use Only</h4>
                    <p className="text-sm text-yellow-800">
                      This product is intended for laboratory research use only. Not for human or veterinary use.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;